import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import messages from "../../../../assets/locale/messages";
import { applicantsProfileStatus } from "../../../../utils/Constants";
import { renderTextEllipsis } from "../../../../utils/Helpers";
import { UIModal, UIRating } from "../../../../components/ui";
import { getEventsList } from "../../../../network/apis/events";
import { getCompanyPortalEventsList } from "../../../../network/apis/companyPortalEvents";
import { flattenCompanyPortalEventsList } from "../../../../utils/companyPortal/portalEventsList";
import { ApplicantProfileHeader } from "./ApplicantProfileLayout";
import ApplicantProfileHeaderActions from "./ApplicantProfileHeaderActions";
import { readApplicantDisplayName } from "./profileHelpers";

function ApplicantProfilePageHeader({
  applicantId,
  portalVariant,
  profile,
  showEditButton,
}) {
  const isAdminPortal = portalVariant === "admin-portal";
  const lang = useSelector((state) => state.locale.lang);
  const { applicants, shared } = messages[lang];
  const copy = applicants.companyApplicantProfile;
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [applicantEvents, setApplicantEvents] = useState([]);

  useEffect(() => {
    if (!showEventsModal) return;
    const params = {
      page: 1,
      items: 100,
      user_id: applicantId,
      status: "ongoing,upcoming,past",
    };
    (async () => {
      try {
        if (isAdminPortal) {
          const response = await getEventsList(params);
          setApplicantEvents(response.data.data);
        } else {
          const response = await getCompanyPortalEventsList(params);
          setApplicantEvents(flattenCompanyPortalEventsList(response));
        }
      } catch {
        setApplicantEvents([]);
      }
    })();
  }, [showEventsModal]);

  const displayName = readApplicantDisplayName(profile, lang);
  const isVerified =
    profile?.profile_status === applicantsProfileStatus.verified ||
    profile?.profile_status === applicantsProfileStatus.profileCompleted;

  const subtitle = [
    `${shared.id} #${profile?.id ?? applicantId}`,
    profile?.role?.name ? `${copy.roleLabel}: ${profile.role.name}` : null,
    profile?.rating != null ? (
      <span className="company-applicant-profile__header-rating" key="rating">
        <UIRating variant="compact" value={profile.rating} preserveValue />
      </span>
    ) : null,
  ].filter(Boolean);

  const workingHours =
    profile?.total_working_hours || profile?.working_hours || "0h 00m";
  const attendanceValue =
    profile?.attendance_percentage ??
    profile?.attendance_rate ??
    profile?.attendance;
  const eventsCount = profile?.participated_in_events_count ?? 0;

  return (
    <>
      <ApplicantProfileHeader
        actions={
          <ApplicantProfileHeaderActions
            applicantId={applicantId}
            portalVariant={portalVariant}
            profile={profile}
            showEditButton={showEditButton}
          />
        }
        avatar={{
          src: profile?.image,
          name: displayName,
          size: "xl",
        }}
        copy={copy}
        headerStats={{
          attendanceValue,
          eventsCount,
          eventsLabel: shared.events,
          workingHours,
        }}
        isAssigned={Boolean(profile?.is_assigned_to_event)}
        isVerified={isVerified}
        onEventsClick={eventsCount > 0 ? () => setShowEventsModal(true) : null}
        subtitle={subtitle}
        title={displayName || "—"}
      />
      <UIModal
        open={showEventsModal}
        type="info"
        title={applicants.applicantDetails.events}
        cancelLabel={shared.close}
        onClose={() => setShowEventsModal(false)}
        onCancel={() => setShowEventsModal(false)}
        maxWidth={false}
        PaperProps={{ className: "applicant-events-modal" }}
        aria-labelledby="applicant-events-modal-title"
      >
        {applicantEvents?.length ? (
          <div className="applicant-events-modal__list">
            {applicantEvents.map((event) => (
              <div className="applicant-events-modal__row" key={event.id}>
                <span className="applicant-events-modal__dot" aria-hidden="true" />
                <div className="applicant-events-modal__row-text">
                  <span className="applicant-events-modal__name">
                    {renderTextEllipsis(event.name)}
                  </span>
                  <span className="applicant-events-modal__code">
                    {`#EVT-${String(event.id).padStart(4, "0")}`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="mb-0 text-body-md">{shared.noData}</p>
        )}
      </UIModal>
    </>
  );
}

ApplicantProfilePageHeader.propTypes = {
  applicantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  portalVariant: PropTypes.oneOf(["admin-portal", "company-portal"]).isRequired,
  profile: PropTypes.object,
  showEditButton: PropTypes.bool,
};

ApplicantProfilePageHeader.defaultProps = {
  profile: null,
  showEditButton: true,
};

export default ApplicantProfilePageHeader;
